import { diag, DiagConsoleLogger, DiagLogLevel, trace } from '@opentelemetry/api';
import { BatchSpanProcessor, WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { BatchLogRecordProcessor, LoggerProvider } from '@opentelemetry/sdk-logs';
import { OTLPLogExporter } from '@opentelemetry/exporter-logs-otlp-http';
import { logs } from '@opentelemetry/api-logs';
import { ConnectionCheckingTracer, ITracer, TRACER_NAME } from 'app/tracing/tracing';
import { ConnectionCheckingLogger, ILogger, LOGGER_NAME, OpenTelemetryLoggingLogger } from 'app/tracing/logging';
import { UnityUtils } from 'app/unity/UnityUtils';
import { UnityRequests } from 'app/unity/UnityRequests';
import { WMTSUtils } from 'app/tiling/WMTSUtils';
import { MapTileFetcher } from 'app/tiling/MapTileFetcher';
import { UnityNotificationListener } from 'app/unity/UnityNotificationListener';
import { WMTSTileSetFetcher } from 'app/tiling/WMTSTileSetFetcher';

import AppContext from './AppContext';
import EventBus from '../eventbus/EventBus';
import { FlightEnricher } from '../util/FlightEnricher';

export class NMUI4DServiceFactory {
    // TODO(bjarne): Authorization headers might need to be set here depending on the OTel collector.
    private OTEL_HEADERS: Record<string, string> = {};

    private logger: ILogger;
    private tracer: ITracer;
    private unityUtils: UnityUtils;
    private unityRequests: UnityRequests;
    private mapTileFetcher: MapTileFetcher;
    private unityNotificationListener: UnityNotificationListener;
    private eventBus: EventBus;
    private flightEnricher: FlightEnricher;

    public getLogger() {
        if (!this.logger) {
            if (!import.meta.env.VITE_OTEL_EXPORTER_ENDPOINT) {
                throw new Error(
                    'No OpenTelemetry exporter/collector endpoint set. Did you forget to configure it at build time?',
                );
            }

            diag.setLogger(new DiagConsoleLogger(), DiagLogLevel.INFO);

            const targetURL = new URL('v1/logs', import.meta.env.VITE_OTEL_EXPORTER_ENDPOINT as string);
            const loggerProvider = new LoggerProvider({
                processors: [
                    // new BatchLogRecordProcessor(new ConsoleLogRecordExporter()),
                    new BatchLogRecordProcessor(
                        new OTLPLogExporter({
                            url: targetURL.href,
                            headers: this.OTEL_HEADERS,
                        }),
                    ),
                ],
            });
            logs.setGlobalLoggerProvider(loggerProvider);

            this.logger = new ConnectionCheckingLogger(
                new OpenTelemetryLoggingLogger(loggerProvider.getLogger(LOGGER_NAME)),
                targetURL,
            );
        }

        return this.logger;
    }

    public getTracer() {
        if (!this.tracer) {
            if (!import.meta.env.VITE_OTEL_EXPORTER_ENDPOINT) {
                throw new Error(
                    'No OpenTelemetry exporter/collector endpoint set. Did you forget to configure it at build time?',
                );
            }

            const targetURL = new URL('v1/traces', import.meta.env.VITE_OTEL_EXPORTER_ENDPOINT as string);
            const provider = new WebTracerProvider({
                spanProcessors: [
                    // new BatchSpanProcessor(new ConsoleSpanExporter()),
                    new BatchSpanProcessor(
                        new OTLPTraceExporter({
                            url: targetURL.href,
                            headers: this.OTEL_HEADERS,
                        }),
                    ),
                ],
            });
            provider.register();

            this.tracer = new ConnectionCheckingTracer(trace.getTracer(TRACER_NAME), targetURL);
        }

        return this.tracer;
    }

    public getUnityUtils() {
        if (!this.unityUtils) {
            this.unityUtils = new UnityUtils();
        }

        return this.unityUtils;
    }

    public getUnityRequests() {
        if (!this.unityRequests) {
            this.unityRequests = new UnityRequests(this.getTracer(), this.getEventBus(), this.getUnityUtils());
        }

        return this.unityRequests;
    }

    public getMapTileFetcher() {
        if (!this.mapTileFetcher) {
            const wmtsUtils = new WMTSUtils();
            const wmtsTileSetFetcher = new WMTSTileSetFetcher(this.getLogger(), this.getTracer(), wmtsUtils);
            this.mapTileFetcher = new MapTileFetcher(this.getLogger(), this.getTracer(), wmtsUtils, wmtsTileSetFetcher);
        }

        return this.mapTileFetcher;
    }

    public getEventBus() {
        if (!this.eventBus) {
            this.eventBus = AppContext.eventBus;
        }

        return this.eventBus;
    }

    public getUnityNotificationListener() {
        if (!this.unityNotificationListener) {
            this.unityNotificationListener = new UnityNotificationListener(this.getEventBus());
        }

        return this.unityNotificationListener;
    }

    public getFlightEnricher() {
        if (!this.flightEnricher) {
            this.flightEnricher = new FlightEnricher();
        }

        return this.flightEnricher;
    }
}
